import Phaser from 'phaser';
import { NPC } from '../../objects/NPC';
import { Player } from '../../objects/Player';

export interface SafeZoneConfig {
  x: number;
  y: number;
  radius: number;
}

export class Zone5EscortSystem {
  private followers: NPC[] = [];
  private active = false;
  private safeZone: SafeZoneConfig;
  private followSpeed = 130;
  private offsets: { x: number; y: number }[] = [
    { x: -50, y: 15 },
    { x: -80, y: -10 },
  ];

  constructor(_scene: Phaser.Scene, safeZone: SafeZoneConfig) {
    this.safeZone = safeZone;
  }

  startEscort(npcs: NPC[], anchorX: number, anchorY: number): void {
    this.followers = npcs;
    this.active = true;

    npcs.forEach((npc, i) => {
      const offset = this.offsets[i] ?? { x: -60, y: 0 };
      npc.x = anchorX + offset.x;
      npc.y = anchorY + offset.y;
      npc.updateLabels();
      const body = npc.body as Phaser.Physics.Arcade.Body;
      body.enable = false;
      npc.setDepth(9);
      npc.setWalking(false);
    });
  }

  isActive(): boolean {
    return this.active;
  }

  update(player: Player, delta: number): boolean {
    if (!this.active || this.followers.length === 0) return false;

    const dt = delta / 1000;
    this.followers.forEach((npc, i) => {
      const offset = this.offsets[i] ?? { x: -60, y: 0 };
      const targetX = player.x + offset.x;
      const targetY = player.y + offset.y;
      const dist = Phaser.Math.Distance.Between(npc.x, npc.y, targetX, targetY);

      if (dist > 20) {
        const angle = Phaser.Math.Angle.Between(npc.x, npc.y, targetX, targetY);
        npc.x += Math.cos(angle) * this.followSpeed * dt;
        npc.y += Math.sin(angle) * this.followSpeed * dt;
        npc.setWalking(true, Math.cos(angle) < 0);
      } else {
        npc.setWalking(false);
      }
      npc.updateLabels();
    });

    const playerNear = Phaser.Math.Distance.Between(
      player.x, player.y, this.safeZone.x, this.safeZone.y,
    ) <= this.safeZone.radius + 40;

    const followersNearPlayer = this.followers.every((npc) => (
      Phaser.Math.Distance.Between(npc.x, npc.y, player.x, player.y) <= 140
    ));

    const allArrived = this.followers.every((npc) => {
      const d = Phaser.Math.Distance.Between(npc.x, npc.y, this.safeZone.x, this.safeZone.y);
      return d <= this.safeZone.radius + 20;
    });

    if (playerNear && (allArrived || followersNearPlayer)) {
      this.followers.forEach((npc, i) => {
        npc.x = this.safeZone.x + (i === 0 ? -35 : 35);
        npc.y = this.safeZone.y + 10;
        npc.setWalking(false);
        npc.updateLabels();
      });
      this.active = false;
      return true;
    }

    return false;
  }

  destroy(): void {
    this.followers.forEach((npc) => npc.setWalking(false));
    this.followers = [];
    this.active = false;
  }
}
